import React from 'react';
import { motion } from 'framer-motion';
import { Phone } from 'lucide-react';
import Img from '@/components/shared/Img';
import QuoteWizard from '@/components/shared/QuoteWizard';

export default function HeroSection() {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-[#0a0a0a]">

      {/* Background image with Ken Burns zoom */}
      <div className="absolute inset-0">
        <Img
          src="/images/fdd1d9e02_generated_a5d96e03.png"
          alt="Premium residential remodeling and construction in Rockport, Texas by Terza Costa Construction"
          className="w-full h-full object-cover opacity-30"
          sizes="100vw"
          priority
          style={{ animation: 'kenBurns 20s ease-in-out infinite alternate' }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a]/95 via-[#0a0a0a]/70 to-[#0a0a0a]/40" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/40 via-transparent to-[#0a0a0a]/60" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-5 md:px-8 pt-24 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">

          {/* LEFT — Branding & Info */}
          <motion.div
            initial={{ x: -24 }}
            animate={{ x: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
            className="flex flex-col items-center text-center lg:items-start lg:text-left"
          >
            {/* Wordmark */}
            <motion.div
              initial={{ y: -8 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              className="mb-6"
            >
              <Img
                src="/images/93d10913f_terza.png"
                alt="Terza Costa"
                className="w-64 sm:w-80 md:w-[400px] opacity-90 object-contain"
                sizes="(min-width: 768px) 400px, (min-width: 640px) 320px, 256px"
                priority
              />
            </motion.div>

            {/* Location bar */}
            <div className="flex items-center gap-3 mb-8">
              <div className="h-px w-8 bg-[#C97B2E] hidden lg:block" />
              <span className="font-body text-white/50 text-xs font-bold uppercase tracking-[0.35em]">
                Rockport · Corpus Christi · The Coastal Bend
              </span>
            </div>

            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold text-white leading-[1.05] mb-5">
              Remodeling &<br />Construction
              <br />
              <span className="italic text-white/80">Built Right. Every Detail.</span>
            </h1>

            {/* Animated underline */}
            <motion.div
              className="h-1 bg-[#C97B2E] mb-7 mx-auto lg:mx-0"
              initial={{ width: 0 }}
              animate={{ width: 80 }}
              transition={{ duration: 0.6, delay: 0.35, ease: 'easeOut' }}
            />

            <p className="font-body text-white/55 text-base md:text-lg leading-relaxed mb-8 max-w-lg">
              Rockport's trusted remodeling and construction contractor — honest pricing, 
              clean job sites, and craftsmanship that shows.
            </p>

            <a
              href="tel:2812037688"
              className="inline-flex items-center gap-3 bg-white/10 border border-white/25 text-white px-7 py-3.5 rounded-sm font-body font-bold uppercase tracking-wider text-sm transition-all duration-300 hover:bg-white/20"
              style={{ boxShadow: '0 0 18px 4px rgba(255,255,255,0.08), 0 0 40px 8px rgba(255,255,255,0.04)' }}
            >
              <Phone className="w-4 h-4" aria-hidden="true" />
              Call Now
            </a>
          </motion.div>

          {/* RIGHT — Contact Form */}
          <motion.div
            initial={{ x: 24 }}
            animate={{ x: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: 'easeOut' }}
            className="lg:max-w-md w-full"
          >
            <div className="bg-[#0a0a0a]/80 backdrop-blur-sm border border-white/10 rounded-sm p-8 md:p-10">
              <div className="mb-7">
                <div className="w-8 h-px bg-[#C97B2E] mb-4" />
                <h2 className="font-heading text-2xl font-bold text-white mb-1.5">
                  Get a Free Estimate
                </h2>
                <p className="font-body text-white/60 text-sm">
                  Response within 1 business day. No pressure.
                </p>
              </div>

              <QuoteWizard formName="hero-estimate" variant="compact" />
            </div>
          </motion.div>

        </div>
      </div>

    </section>
  );
}